import React, { useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { IconButton, TextField, Tooltip } from '@mui/material';

import type { BoardContext } from './BlockView';
import { activeTab } from './lensTabs';
import type { PageEntry, Tab, Tabs } from './types';

type TabsViewProps = {
  group: Tabs;
  context: BoardContext;
  renderPage: (page: Array<PageEntry>) => React.ReactNode;
};

const KEYS: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
};

function TitleInput({ label, initial, onCommit, onCancel }: { label: string; initial: string; onCommit: (title: string) => void; onCancel: () => void }) {
  const [title, setTitle] = useState(initial);
  return (
    <TextField
      autoFocus
      inputProps={{ 'aria-label': label }}
      onBlur={onCancel}
      onChange={(event) => setTitle(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && title.trim()) onCommit(title.trim());
        if (event.key === 'Escape') onCancel();
      }}
      placeholder="Tab title"
      size="small"
      value={title}
      variant="standard"
    />
  );
}

export default function TabsView({ group, context, renderPage }: TabsViewProps) {
  const control = context.tabs;
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});
  const [naming, setNaming] = useState<'new' | 'rename' | null>(null);
  if (control?.every)
    return (
      <>
        {group.tabs.map((tab) => (
          <section className="section tab-page" key={tab.id}>
            <div className="sh">
              <h2>{tab.tab}</h2>
            </div>
            {renderPage(tab.page)}
          </section>
        ))}
      </>
    );

  const active = activeTab(group, control?.chosen ?? []);
  const index = group.tabs.indexOf(active);
  const show = (tab: Tab) => control?.choose(group, tab.id);
  const editor = context.editor;
  const apply = (operation: Parameters<NonNullable<BoardContext['editor']>['apply']>[0]) => {
    setNaming(null);
    editor?.apply(operation);
  };
  const panelId = `${group.id}-panel`;

  return (
    <section className="tabs" data-lens-tabs={group.id}>
      <div className="tab-strip">
        {control?.narrow ? (
          <select aria-label="Page" className="tab-select" onChange={(event) => show(group.tabs.find((tab) => tab.id === event.target.value) ?? active)} value={active.id}>
            {group.tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.tab}
              </option>
            ))}
          </select>
        ) : (
          <div
            aria-label="Pages"
            className="tab-bar"
            onKeyDown={(event) => {
              const step = KEYS[event.key];
              if (!step || event.target instanceof HTMLInputElement) return;
              event.preventDefault();
              const next = group.tabs[step(index, group.tabs.length)];
              show(next);
              buttons.current[next.id]?.focus();
            }}
            role="tablist"
          >
            {group.tabs.map((tab) =>
              naming === 'rename' && tab === active ? (
                <TitleInput initial={tab.tab} key={tab.id} label="Tab title" onCancel={() => setNaming(null)} onCommit={(title) => apply({ op: 'set', id: tab.id, patch: { tab: title } })} />
              ) : (
                <button
                  aria-controls={panelId}
                  aria-selected={tab === active}
                  className={tab === active ? 'on' : ''}
                  data-lens-tab={tab.id}
                  id={`${group.id}-${tab.id}`}
                  key={tab.id}
                  onClick={() => show(tab)}
                  ref={(node) => {
                    buttons.current[tab.id] = node;
                  }}
                  role="tab"
                  tabIndex={tab === active ? 0 : -1}
                  type="button"
                >
                  {tab.tab}
                </button>
              ),
            )}
          </div>
        )}
        {editor ? (
          <div className="tab-edit" data-lens-tab-edit>
            <span className="tab-icons">
              <Tooltip title="Rename this tab">
                <IconButton aria-label={`Rename the ${active.tab} tab`} disabled={editor.pending} onClick={() => setNaming('rename')} size="small">
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Move left">
                <span>
                  <IconButton aria-label={`Move the ${active.tab} tab left`} disabled={editor.pending || index === 0} onClick={() => apply({ op: 'move', id: active.id, before: group.tabs[index - 1]?.id })} size="small">
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move right">
                <span>
                  <IconButton aria-label={`Move the ${active.tab} tab right`} disabled={editor.pending || index === group.tabs.length - 1} onClick={() => apply({ op: 'move', id: active.id, after: group.tabs[index + 1]?.id })} size="small">
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete this tab and its blocks">
                <IconButton aria-label={`Delete the ${active.tab} tab`} disabled={editor.pending} onClick={() => apply({ op: 'remove', id: active.id })} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
            {naming === 'new' ? (
              <TitleInput initial="" label="New tab title" onCancel={() => setNaming(null)} onCommit={(title) => apply({ op: 'tab', title, after: group.tabs[group.tabs.length - 1].id })} />
            ) : (
              <Tooltip title="Add a tab">
                <IconButton aria-label="Add a tab" disabled={editor.pending} onClick={() => setNaming('new')} size="small">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ) : null}
      </div>
      <div className="tab-panel" id={panelId} role="tabpanel" {...(control?.narrow ? { 'aria-label': active.tab } : { 'aria-labelledby': `${group.id}-${active.id}` })}>
        {active.page.length ? renderPage(active.page) : editor ? <p className="empty">This tab is empty. Add blocks from the chat with “Add … to the {active.tab} tab”.</p> : null}
      </div>
    </section>
  );
}
